import { useMutation } from "@tanstack/react-query";
import type {
  FieldValues as BaseFieldValues,
  DefaultValues,
  FieldErrors,
} from "react-hook-form";
import { useForm, useFormState } from "react-hook-form";

import type { Promisable } from "@core/foundation/promise";
import type { ReadonlyRecord } from "@core/foundation/types";
import type { ZodError, ZodType, z } from "@core/zod";

type ErrorType = "invalid" | "required" | "too_big" | "too_small";

type ErrorMapping = ReadonlyRecord<
  string,
  Partial<ReadonlyRecord<ErrorType, string>>
>;

// oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useZodForm<
  FieldValues extends BaseFieldValues,
  Schema extends ZodType,
>({
  defaultValues,
  errorMapping,
  onSubmit,
  schema,
  transform,
}: {
  readonly defaultValues: DefaultValues<FieldValues>;
  readonly errorMapping?: ErrorMapping;
  readonly onSubmit: (values: z.infer<Schema>) => Promisable<void>;
  readonly schema: Schema;
  readonly transform: (values: FieldValues) => Partial<z.input<Schema>>;
}) {
  const form = useForm<FieldValues>({
    defaultValues,
    mode: "onChange",
    resolver: values => {
      const result = schema.safeParse(transform(values));

      if (result.success) {
        return { errors: {}, values };
      }

      return {
        errors: zodErrorToFieldErrors<FieldValues>(result.error, errorMapping),
        values: {},
      };
    },
  });

  const handleSubmit = useMutation({
    mutationFn: form.handleSubmit(async (rawValues: FieldValues) =>
      onSubmit(schema.parse(transform(rawValues))),
    ),
  });

  const state = useFormState({ control: form.control });

  return {
    control: form.control,
    handleSubmit: handleSubmit.mutate,
    register: form.register,
    reset: form.reset,
    state,
  };
}

function zodErrorToFieldErrors<T extends BaseFieldValues>(
  error: ZodError,
  errorMapping?: ErrorMapping,
): FieldErrors<T> {
  // @mutable
  const errors: FieldErrors<Record<string, unknown>> = {};

  for (const issue of error.issues) {
    const fieldName = issue.path[0];

    if (typeof fieldName === "string") {
      let errorType: ErrorType;

      switch (issue.code) {
        case "custom":
        case "invalid_element":
        case "invalid_format":
        case "invalid_key":
        case "invalid_union":
        case "invalid_value":
        case "not_multiple_of":
        case "unrecognized_keys":
          errorType = "invalid";
          break;
        case "invalid_type":
          errorType =
            issue.message.includes("received null") ||
            issue.message.includes("received undefined")
              ? "required"
              : "invalid";
          break;
        case "too_big":
          errorType = "too_big";
          break;
        case "too_small":
          errorType = "too_small";
          break;
      }

      errors[fieldName] ??= {
        message: errorMapping?.[fieldName]?.[errorType] ?? issue.message,
        type: issue.code,
      };
    }
  }

  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return errors as FieldErrors<T>;
}
